import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Header from './Header';
import Footer from './Footer';
import cssClass from './RoadmapAccessSharing.module.css';
import searchIcon from '../assets/access-management/search.svg';
import closeIcon from '../assets/access-management/x.svg';
import uploadIcon from '../assets/access-management/upload.svg';
import userIcon from '../assets/access-management/user.svg';
import questionMarkIcon from '../assets/access-management/question-mark-icon-help.svg';
import filterIcon from '../assets/access-management/filter.svg';
import copyLinkIcon from '../assets/access-management/copy-link.svg';
import { UserContext } from '../context/UserContext';
import { AnimatePresence, motion } from 'framer-motion';
import sampleCSV from '../assets/access-management/sample-csv.csv';
import { Tooltip } from 'react-tooltip';
import { v4 as uuidv4 } from 'uuid';
import { useNotification } from '../hooks/useNotification';
import { changePageTitle } from '../utils/constants';
import { useConfirm } from '../context/ConfirmationContext';

function debounce(func, delay) {
  let timer;
  const debouncedFn = function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => func.apply(this, args), delay);
  };

  // Add a cancel method to the debounced function
  debouncedFn.cancel = function () {
    clearTimeout(timer);
  };

  return debouncedFn;
}

const TABS = [
  { name: 'Grant Access', value: 'grant' },
  { name: 'Already Shared With', value: 'shared' },
  { name: 'Grant Editor access', value: 'editor_access' },
];

// Animation variants
const animationVariants = {
  backdrop: {
    hidden: {
      opacity: 0,
      transition: { duration: 0.2 },
    },
    visible: {
      opacity: 1,
      transition: { duration: 0.2 },
    },
  },
  modal: {
    hidden: {
      // y: 20,
      scale: 0.95,
      opacity: 0,
      transition: {
        duration: 0.2,
        ease: 'easeInOut',
      },
    },
    visible: {
      // y: 0,
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.2,
        ease: 'easeOut',
      },
    },
  },
};


/**
 *
 * @returns {JSX.Element}
 * @description This component is used to manage the access of roadmaps. It allows users to search for roadmaps, grant access to other users, and view the users with whom the roadmap has already been shared.
 * @component
 * @example
 * <RoadmapAccessSharing />
 */
const RoadmapAccessSharing = ({ source='dashboard', currentRoadmap={}, onCancel }) => {
  const { userDetails } = useContext(UserContext);
  const {
    showNotification,
    showLoadingNotification,
    resolveNotification,
    rejectNotification,
  } = useNotification();

  // State for the roadmap search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchBar, setShowSearchBar] = useState(true);
  const [selectedRoadmap, setSelectedRoadmap] = useState(null);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // State for the tabs
  const [activeTab, setActiveTab] = useState('grant');

  // State for emails
  const [emailInput, setEmailInput] = useState('');
  const [emails, setEmails] = useState([]);
  const [showAddEmailButton, setShowAddEmailButton] = useState(false);
  const [emailSearchInput, setEmailSearchInput] = useState('');
  const [notifyPeopleChecked, setNotifyPeopleChecked] = useState(false);

  // State for shared users
  const [sharedUsers, setSharedUsers] = useState([]);
  const [sharedUserSearchInput, setSharedUserSearchInput] = useState('');
  const [sharedUserRoleFilterName, setSharedUserRoleFilterName] =
    useState('all');

  // State for editor access tab
  const [editorEmailInput, setEditorEmailInput] = useState('');
  const [editorEmails, setEditorEmails] = useState([]);
  const [editorEmailSearchInput, setEditorEmailSearchInput] = useState('');
  const [notifyEditorChecked, setNotifyEditorChecked] = useState(false);

  // State for api calling indecator
  const [isApiCalling, setIsApicalling] = useState(false);

  const [filterMenuOpen, setFilterMenuOpen] = useState(false);

  // Ref for file input
  const fileInputRef = useRef(null);
  const dropAreaRef = useRef(null);

  // Ref for search
  const searchRef = useRef(null);

  // API call to get dearch result against query
  const getSearchResult = async (query) => {
    try {
      const data = {};
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/r/search?e=${userDetails?.email}&query=${query}`,
        {
          method: 'POST',
          cache: 'no-cache',
          // credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        },
      );
      const responseData = await response.json();
      if (response.status === 200) {
        const searchResult = responseData?.result || [];
        if (searchResult.length !== 0) {
          setSearchResults(searchResult);
          setShowSearchResults(true);
        }
      }
    } catch (error) {
    } finally {
    }
  };

  /**
   * Debounced function to handle search input change
   * @param {string} query - The search query entered by the user.
   */
  const debouncedSearch = useCallback(
    debounce((query) => {
      if (query.trim() !== '') {
        getSearchResult(query);
      }
    }, 500),
    [],
  );

  // Handle search input change
  const handleSearchChange = async (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.trim() === '') {
      // Cancel any pending debounced call
      if (searchRef.current && searchRef.current.cancel) {
        searchRef.current.cancel();
      }
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    debouncedSearch(query);
  };

  // Handle roadmap selection
  const handleRoadmapSelect = (roadmap) => {
    setSelectedRoadmap(roadmap);
    setSearchQuery('');
    setShowSearchResults(false);
  };

  // Handle email input change
  const handleEmailInputChange = (e) => {
    setEmailInput(e.target.value.toLowerCase());
    setShowAddEmailButton(
      validateEmail(e.target.value) && userDetails?.email !== e.target.value,
    );
  };

  // Handle email input keydown (Enter)
  const handleEmailInputKeyDown = (e) => {
    if (e.key === 'Enter' && emailInput.trim() !== '') {
      if (validateEmail(emailInput) && userDetails?.email !== emailInput) {
        addEmail(emailInput);
        setEmailInput('');
        setShowAddEmailButton(false);
      }
    }
  };

  // Validate email format
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Add email to the list
  const addEmail = (email) => {
    if (!emails.includes(email)) {
      setEmails([...emails, email]);
    }
  };

  // Remove email from the list
  const removeEmail = (email) => {
    setEmails(emails.filter((e) => e !== email));
  };

  // Handle CSV upload button click
  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAddEmailClick = () => {
    if (validateEmail(emailInput) && userDetails?.email !== emailInput) {
      addEmail(emailInput);
      setEmailInput('');
      setShowAddEmailButton(false);
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      processCSVFile(file);
    }
    fileInputRef.current.value = '';
  };

  // Process CSV file
  const processCSVFile = (file) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const content = e.target?.result;
      const rows = content
        .split('\n')
        .map((row) => row.trim())
        .filter((row) => row);

      if (rows.length < 2) return;
      const headers = rows[0].split(',').map((h) => h.trim());

      const emailIndex = headers.indexOf('email'); // Find index of "email" column

      if (emailIndex === -1) {
        showNotification('error', 'Email column not found!');
        return;
      }
      const newEmails = [];

      rows.slice(1).map((row) => {
        const columns = row.split(',').map((col) => col.trim());
        const email = columns[emailIndex].toLowerCase();
        if (
          email &&
          validateEmail(email) &&
          !emails.includes(email) &&
          userDetails?.email !== email
        ) {
          newEmails.push(email);
        }
      });

      if (newEmails.length > 0) {
        setEmails([...emails, ...newEmails]);
      }
    };

    reader.readAsText(file);
  };

  const handleSampleCSVDownload = () => {
    const link = document.createElement('a');
    link.href = sampleCSV;
    link.download = 'access-management-sample-user-csv-for-setu-roadmap.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    if (dropAreaRef.current) {
      dropAreaRef.current.classList.add(cssClass.dragOver);
    }
  };

  // Handle drag leave
  const handleDragLeave = (e) => {
    e.preventDefault();
    if (dropAreaRef.current) {
      dropAreaRef.current.classList.remove(cssClass.dragOver);
    }
  };

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();

    if (dropAreaRef.current) {
      dropAreaRef.current.classList.remove(cssClass.dragOver);
    }

    const files = e.dataTransfer.files;

    if (files.length > 0) {
      const file = files[0];
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        processCSVFile(file);
      }
    }
  };

  const handleCloseClick = () => {
    setSelectedRoadmap(null);
    setActiveTab('grant');
    setEmails([]);
    setSharedUsers([]);
    setEmailSearchInput('');
    setSharedUserSearchInput('');
    setSharedUserRoleFilterName('all');
    setFilterMenuOpen(false);
  };

  // Handle done button click
  const handleDoneClick = async () => {
    // show notification to user
    const toastId = uuidv4();
    showLoadingNotification(toastId, 'Granting access. Please wait');

    try {
      setIsApicalling(true);
      const data = {
        r_id: selectedRoadmap?.r_id,
        g_e: userDetails?.email,
        access: emails.reduce((res, email) => {
          res.push({
            e: email,
            level: 'viewer',
            sharing_mode: 'access_shared_by_admin',
          });
          return res;
        }, []),
        notify_people: notifyPeopleChecked,
      };

      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/r/grant-access`,
        {
          method: 'POST',
          cache: 'no-cache',
          // credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        },
      );
      const responseData = await response.json();
      if (response.status === 200) {
        resolveNotification(toastId, `${responseData?.message}`);
        setEmails([]);
      } else {
        rejectNotification(toastId, `${responseData?.error}`);
      }
    } catch (error) {
      rejectNotification(toastId, 'Something went wrong. Please try again');
    } finally {
      setIsApicalling(false);
      setNotifyPeopleChecked(false);
    }
  };

  /**
   *
   * @param {Object} user is the user object containing user details
   * @description This function is used to revoke access from a user. It sends a request to the server to revoke access for the specified user and updates the shared users list accordingly.
   */
  const confirm = useConfirm();

  const handleUserAccessRevoke = async (user) => {
    const confirmed = await confirm({
      type: 'revoke-access-confirmation',
      title: 'Access Revoke Confirmation',
      message: `Are you sure you want to revoke access of <strong>${user.email}</strong> to <strong>${selectedRoadmap?.title} roadmap</strong>?`,
    });

    if (confirmed) {
      // show notification to user
      const toastId = uuidv4();
      showLoadingNotification(toastId, 'Revoking access. Please wait');

      try {
        const data = {
          r_e: userDetails?.email,
          r_id: selectedRoadmap?.r_id,
          target: [user.email],
        };

        const response = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/r/revoke-access`,
          {
            method: 'POST',
            cache: 'no-cache',
            // credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
          },
        );
        const responseData = await response.json();
        if (response.status === 200) {
          resolveNotification(toastId, `${responseData?.message}`);
          setSharedUsers((prev) => prev.filter((u) => u.email !== user.email));
        } else {
          rejectNotification(toastId, `${responseData?.error}`);
        }
      } catch (error) {
        rejectNotification(toastId, 'Something went wrong. Please try again');
      } finally {
      }
    }
  };

  // Editor Access tab
  // Handle email input change
  const handleEditorEmailInputChange = (e) => {
    setEditorEmailInput(e.target.value.toLowerCase());
  };

  // Handle email input keydown (Enter)
  const handleEditorEmailInputKeyDown = (e) => {
    if (e.key === 'Enter' && editorEmailInput.trim() !== '') {
      if (
        validateEmail(editorEmailInput) &&
        userDetails?.email !== editorEmailInput
      ) {
        addEditorEmail(editorEmailInput);
        setEditorEmailInput('');
      }
    }
  };

  const handleAddEditorEmailClick = () => {
    if (
      validateEmail(editorEmailInput) &&
      userDetails?.email !== editorEmailInput
    ) {
      addEditorEmail(editorEmailInput);
      setEditorEmailInput('');
    }
  };

  // Add editor email to the list
  const addEditorEmail = (email) => {
    if (!editorEmails.includes(email)) {
      setEditorEmails([...editorEmails, email]);
    }
  };

  // Remove editor email from the list
  const removeEditorEmail = (email) => {
    setEditorEmails(editorEmails.filter((e) => e !== email));
  };

  // Handle done button click
  const handleEditorAccessGranting = async () => {
    // show notification to user
    const toastId = uuidv4();
    showLoadingNotification(toastId, 'Granting editor access. Please wait');

    try {
      setIsApicalling(true);
      const data = {
        r_id: selectedRoadmap?.r_id,
        g_e: userDetails?.email,
        access: editorEmails.reduce((res, email) => {
          res.push({
            e: email,
            level: 'editor',
            sharing_mode: 'collaboration',
          });
          return res;
        }, []),
        notify_people: notifyEditorChecked,
      };

      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/r/grant-access`,
        {
          method: 'POST',
          cache: 'no-cache',
          // credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        },
      );
      const responseData = await response.json();
      if (response.status === 200) {
        resolveNotification(toastId, `${responseData?.message}`);
        setEditorEmails([]);
      } else {
        rejectNotification(toastId, `${responseData?.error}`);
      }
    } catch (error) {
      rejectNotification(toastId, 'Something went wrong. Please try again');
    } finally {
      setIsApicalling(false);
      setNotifyEditorChecked(false);
    }
  };

  const handleClickOnBackdrop = (e) => {
    e.stopPropagation();
    onCancel();
  };

  const visibleTabs = TABS.filter((tab, index) => {
    if (tab.value === 'editor_access') {
      return selectedRoadmap?.owner;
    }
    return true;
  });

  const filteredEmailList = useMemo(() => {
    if (!emailSearchInput) return emails;
    return emails.filter((email) =>
      email.toLowerCase().includes(emailSearchInput.toLowerCase()),
    );
  }, [emails, emailSearchInput]);

  const filteredEditorEmailList = useMemo(() => {
    if (!editorEmailSearchInput) return editorEmails;
    return editorEmails.filter((email) =>
      email.toLowerCase().includes(editorEmailSearchInput.toLowerCase()),
    );
  }, [editorEmails, editorEmailSearchInput]);

  const filteredSharedUserList = useMemo(() => {
    if (!sharedUserSearchInput && sharedUserRoleFilterName === 'all') {
      return sharedUsers;
    }
    return sharedUsers.filter((user) => {
      const result = user.email
        .toLowerCase()
        .includes(sharedUserSearchInput.toLowerCase());
      const matchesRole =
        sharedUserRoleFilterName === 'all'
          ? true
          : sharedUserRoleFilterName === 'editor'
            ? ['editor', 'owner'].includes(user.access)
            : user.access === sharedUserRoleFilterName;
      return result && matchesRole;
    });
  }, [sharedUsers, sharedUserSearchInput, sharedUserRoleFilterName]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      const target = e.target;
      const searchContainer = document.getElementById('search-container');

      if (searchContainer && !searchContainer.contains(target)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    searchRef.current = debouncedSearch;
    return () => {
      if (searchRef.current && searchRef.current.cancel) {
        searchRef.current.cancel();
      }
    };
  }, [debouncedSearch]);

  useEffect(() => {
    const getRoadmapAccessData = async () => {
      try {
        const data = {
          r_id: selectedRoadmap?.r_id,
          e: userDetails?.email,
        };

        const response = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/r/access-data`,
          {
            method: 'POST',
            cache: 'no-cache',
            // credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
          },
        );
        const responseData = await response.json();
        if (response.status === 200) {
          setSharedUsers(responseData?.users || []);
        }
      } catch (error) {
        console.log(error);
      } finally {
      }
    };
    if (activeTab === 'shared' && userDetails && selectedRoadmap) {
      getRoadmapAccessData();
    }
  }, [activeTab, userDetails, selectedRoadmap]);

  useEffect(() => {
    if(source === 'canvas') {
      setShowSearchBar(false);
      setSelectedRoadmap(currentRoadmap);
    }
  }, [source])

  return (
    <>
      {/* <Header /> */}
      <motion.div className={cssClass.container} onClick={handleClickOnBackdrop} variants={animationVariants.backdrop} initial='hidden' animate='visible' exit='hidden' key='backdrop' >
        <motion.div
          className={cssClass.accessPage}
          onClick={(e) => e.stopPropagation()}
          variants={animationVariants.modal} initial='hidden' animate='visible' exit='hidden' key='modal'
        >
          <h1 className={cssClass.title}>Manage Roadmap Access</h1>

          {/* Roadmap Search */}
          {
            showSearchBar && (
            <div id="search-container" className={cssClass.searchContainer}>
              {selectedRoadmap ? (
                <div className={cssClass.selectedRoadmap}>
                  <span>{selectedRoadmap?.title}</span>
                  <button
                    className={cssClass.clearButton}
                    onClick={() => handleCloseClick()}
                  >
                    {/* <X size={16} /> */}
                    <img src={closeIcon} alt="" className={cssClass.icon} />
                  </button>
                </div>
              ) : (
                <>
                  <div
                    className={`${cssClass.searchInputWrapper} ${showSearchResults ? cssClass.searchInputWrapperBorderChange : ''}`}
                  >
                    {/* <Search className={cssClass.searchIcon} size={18} /> */}
                    <img
                      src={searchIcon}
                      alt=""
                      className={cssClass.searchIcon}
                    />
                    <input
                      type="text"
                      className={cssClass.searchInput}
                      placeholder="Search for a roadmap..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                    />
                  </div>

                  {showSearchResults && searchResults.length > 0 && (
                    <div className={cssClass.searchResultsParent}>
                      <div className={cssClass.searchResults}>
                        {searchResults.map((roadmap, index) => (
                          <div
                            key={index}
                            className={cssClass.searchResultItem}
                            onClick={() => handleRoadmapSelect(roadmap)}
                          >
                            {roadmap?.title}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

          )}

          <>
            {/* Tabs */}
            <div className={cssClass.tabs}>
              {visibleTabs.map((tab, index) => (
                <button
                  key={tab.value}
                  className={`${cssClass.tab} ${tab.value === activeTab ? cssClass.active : ''} `}
                  onClick={() => setActiveTab(tab.value)}
                >
                  {tab.name}
                  {activeTab === tab.value && (
                    <motion.div
                      // layoutId="active-tab"
                      className={cssClass.activeTab}
                    />
                  )}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className={cssClass.tabContent}>
              {activeTab === 'grant' ? (
                <div className={cssClass.grantAccessTab}>
                  <div className={cssClass.emailInputContainer}>
                    <input
                      type="email"
                      className={cssClass.emailInput}
                      placeholder="Enter email address and press Enter"
                      value={emailInput}
                      onChange={handleEmailInputChange}
                      onKeyDown={handleEmailInputKeyDown}
                      disabled={!selectedRoadmap}
                    />
                    {showAddEmailButton && (
                      <button
                        className={cssClass.uploadButton}
                        onClick={handleAddEmailClick}
                        disabled={!selectedRoadmap}
                      >
                        <span>Add Email</span>
                      </button>
                    )}
                    <button
                      className={cssClass.uploadButton}
                      onClick={handleUploadClick}
                      disabled={!selectedRoadmap}
                    >
                      {/* <Upload size={16} /> */}
                      <img src={uploadIcon} alt="" className={cssClass.icon} />
                      <span>Upload CSV</span>
                    </button>
                    {emails.length > 0 && (
                      <button
                        className={cssClass.uploadButton}
                        onClick={() => setEmails([])}
                      >
                        <span>Clear All</span>
                      </button>
                    )}
                    <input
                      type="file"
                      ref={fileInputRef}
                      className={cssClass.fileInput}
                      accept=".csv"
                      onChange={handleFileChange}
                    />
                  </div>

                  <div
                    ref={dropAreaRef}
                    className={cssClass.emailListContainer}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    {emails.length > 0 && (
                      <div className={cssClass.accessUtilityWrapper}>
                        <AccessUtilityBanner
                          users={emails}
                          searchValue={emailSearchInput}
                          onSearchChange={(e) =>
                            setEmailSearchInput(
                              e.target.value.trim().toLowerCase(),
                            )
                          }
                          resultCount={
                            emailSearchInput !== ''
                              ? filteredEmailList.length
                              : null
                          }
                          filterMenuOpen={filterMenuOpen}
                          setFilterMenuOpen={setFilterMenuOpen}
                        />
                      </div>
                    )}
                    {emails.length > 0 ? (
                      <>
                        <div className={cssClass.emailList}>
                          {filteredEmailList.map((email, index) => (
                            <div key={index} className={cssClass.emailItem}>
                              <div>
                                <span>{email}</span>
                              </div>
                              <button
                                className={cssClass.removeEmailButton}
                                onClick={() => removeEmail(email)}
                              >
                                {/* <X size={14} /> */}
                                <img
                                  src={closeIcon}
                                  alt=""
                                  className={cssClass.icon}
                                />
                              </button>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className={cssClass.dropAreaMessage}>
                        <p>
                          Enter emails above or drag and drop a CSV file here
                        </p>
                        <p>
                          Click here to download
                          <button
                            onClick={handleSampleCSVDownload}
                            className={cssClass.sampleCSVLink}
                          >
                            sample csv
                          </button>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* <div className={cssClass.publicAccessCt}>
                    <div>
                      <label className={cssClass.publicAccessInner}>
                      <input type="checkbox" className={cssClass.publicAccessCheckbox} disabled={!selectedRoadmap} />
                      <span className={cssClass.checkmark}></span>
                      <span className={cssClass.publicAccessSpan1}>Publish on Course Page </span>
                      <span className={cssClass.publicAccessSpan2}>(anyone on the internet can view this)</span>
                      </label>
                    </div>
                    <button className={cssClass.copyLinkBtn} disabled={!selectedRoadmap}>
                      <img src={copyLinkIcon} alt="" />
                      <span>Copy Public Link</span>
                    </button>
                  </div> */}
                  {emails.length > 0 && (
                    <>
                    <div className={cssClass.notifyCt}>
                      <input
                        type="checkbox"
                        name="notify"
                        id="notify"
                        className={cssClass.notifyCheckbox}
                        checked={notifyPeopleChecked}
                        onChange={(e) =>
                          setNotifyPeopleChecked(e.target.checked)
                        }
                      />
                      <p
                        className={cssClass.notifyText}
                        onClick={() =>
                          setNotifyPeopleChecked(!notifyPeopleChecked)
                        }
                      >
                        Notify People
                      </p>
                      <img
                        src={questionMarkIcon}
                        alt=""
                        className={cssClass.helpIcon}
                        data-tooltip-id="access-mgmt-tooltip"
                        data-tooltip-content="Checking this will send a confirmation email stating that they have granted access to the roadmap to above email IDs"
                        data-tooltip-place="top"
                      />
                    </div>
                    </>
                  )}
                </div>
              ) : activeTab === 'shared' ? (
                <div className={cssClass.sharedWithTab}>
                  <div className={cssClass.sharedWithTabInner}>
                    {sharedUsers.length != 0 && (
                      <AccessUtilityBanner
                        users={sharedUsers}
                        searchValue={sharedUserSearchInput}
                        onSearchChange={(e) =>
                          setSharedUserSearchInput(
                            e.target.value.trim().toLowerCase(),
                          )
                        }
                        resultCount={
                          sharedUserSearchInput !== ''
                            ? filteredSharedUserList.length
                            : null
                        }
                        hasFilter={true}
                        setSharedUserRoleFilterName={
                          setSharedUserRoleFilterName
                        }
                        sharedUserRoleFilterName={sharedUserRoleFilterName}
                        filterMenuOpen={filterMenuOpen}
                        setFilterMenuOpen={setFilterMenuOpen}
                      />
                    )}
                    {sharedUsers.length > 0 ? (
                      <div className={cssClass.sharedUsersList}>
                        {filteredSharedUserList.map((user, index) => (
                          <div key={index} className={cssClass.sharedUserItem}>
                            <div className={cssClass.userAvatar}>
                              {/* <User size={18} /> */}
                              <img src={userIcon} alt="" />
                            </div>
                            <div className={cssClass.userInfo}>
                              {/* <div className={cssClass.userName}>
                                  {user.name}
                                </div> */}
                              <div className={cssClass.userEmail}>
                                {user.email}
                                {user.access === 'owner' && (
                                  <span className={cssClass.userOwner}>
                                    owner
                                  </span>
                                )}
                              </div>
                            </div>
                            {user.access !== 'owner' &&
                              user.email !== userDetails.email && (
                                <button
                                  className={cssClass.userRevokeBtn}
                                  onClick={() => handleUserAccessRevoke(user)}
                                >
                                  Revoke
                                </button>
                              )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={cssClass.noSharedUsers}>
                        <p>{!selectedRoadmap ? `Please select a roadmap from search bar above` : `This roadmap hasn't been shared with anyone yet.`}</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className={cssClass.grantAccessTab}>
                  <div className={cssClass.emailInputContainer}>
                    <input
                      type="email"
                      className={cssClass.emailInput}
                      placeholder="Enter email address and press Enter"
                      value={editorEmailInput}
                      onChange={handleEditorEmailInputChange}
                      onKeyDown={handleEditorEmailInputKeyDown}
                      disabled={!selectedRoadmap}
                    />
                    <button
                      className={cssClass.uploadButton}
                      onClick={handleAddEditorEmailClick}
                      disabled={!selectedRoadmap}
                    >
                      <span>Add Email</span>
                    </button>
                    {editorEmails.length > 0 && (
                      <button
                        className={cssClass.uploadButton}
                        onClick={() => setEditorEmails([])}
                      >
                        <span>Clear All</span>
                      </button>
                    )}
                  </div>

                  <div className={cssClass.emailListContainer}>
                    {editorEmails.length > 0 && (
                      <div className={cssClass.accessUtilityWrapper}>
                        <AccessUtilityBanner
                          users={editorEmails}
                          searchValue={editorEmailSearchInput}
                          onSearchChange={(e) =>
                            setEditorEmailSearchInput(
                              e.target.value.trim().toLowerCase(),
                            )
                          }
                          resultCount={
                            editorEmailSearchInput !== ''
                              ? filteredEditorEmailList.length
                              : null
                          }
                          filterMenuOpen={filterMenuOpen}
                          setFilterMenuOpen={setFilterMenuOpen}
                        />
                      </div>
                    )}
                    {editorEmails.length > 0 ? (
                      <div className={cssClass.emailList}>
                        {filteredEditorEmailList.map((email, index) => (
                          <div key={index} className={cssClass.emailItem}>
                            <div>
                              <span>{email}</span>
                            </div>
                            <button
                              className={cssClass.removeEmailButton}
                              onClick={() => removeEditorEmail(email)}
                            >
                              {/* <X size={14} /> */}
                              <img
                                src={closeIcon}
                                alt=""
                                className={cssClass.icon}
                              />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={cssClass.dropAreaMessage}>
                        <p>
                          Enter emails above to grant <strong>Editor</strong>{' '}
                          access
                        </p>
                        <p>
                          Note : Only <strong>Admins</strong> can get editor
                          access
                        </p>
                      </div>
                    )}
                  </div>

                  {editorEmails.length > 0 && (
                    <div className={cssClass.notifyCt}>
                      <input
                        type="checkbox"
                        name="notify"
                        id="notify-editor"
                        className={cssClass.notifyCheckbox}
                        checked={notifyEditorChecked}
                        onChange={(e) =>
                          setNotifyEditorChecked(e.target.checked)
                        }
                      />
                      <p>Notify People</p>
                      <img
                        src={questionMarkIcon}
                        alt=""
                        className={cssClass.helpIcon}
                        data-tooltip-id="access-mgmt-tooltip"
                        data-tooltip-content="Checking this will send the above email IDs a confirmation email stating that they have granted access to the roadmap"
                        data-tooltip-place="top"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
          <div className={cssClass.buttonCt}>
            <button
              className={`${cssClass.btn} ${cssClass.cancel}`}
              onClick={onCancel}
            >
              <span>Cancel</span>
            </button>
            {activeTab !== 'shared' && (
              <button
                className={`${cssClass.btn} ${cssClass.done}`}
                onClick={
                  activeTab === 'grant'
                    ? handleDoneClick
                    : handleEditorAccessGranting
                }
                disabled={
                  activeTab === 'grant'
                    ? emails.length === 0 || isApiCalling
                    : editorEmails.length === 0 || isApiCalling
                }
              >
                <span>Done</span>
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>

      <Tooltip
        id="access-mgmt-tooltip"
        style={{
          backgroundColor: '#000000',
          color: '#FFFFFF',
          fontFamily: 'Montserrat, Arial, sans-serif',
          fontSize: '12px',
          fontWeight: '600',
          padding: '5px',
          zIndex: 1001,
          width: '300px',
        }}
        noArrow
        delayShow={100}
        className={cssClass['custom-tooltip-animation']}
      />
    </>
  );
};

const AccessUtilityBanner = ({
  users,
  searchValue,
  onSearchChange,
  resultCount,
  setSharedUserRoleFilterName,
  sharedUserRoleFilterName,
  hasFilter = false,
  filterMenuOpen,
  setFilterMenuOpen,
}) => {
  const ROLES = ['All', 'Owner', 'Viewer', 'Editor'];
  const filterMenuAnimationVariants = {
    hidden: {
      maxHeight: 0,
      transition: {
        duration: 0.4,
      },
    },
    visible: {
      maxHeight: 300,
      transition: {
        duration: 0.4,
      },
    },
  };

  const getUserCountByLevel = (role) => {
    if (role === 'All') return users.length;
    else if (role === 'Editor')
      return users.filter((u) => ['owner', 'editor'].includes(u.access)).length;
    return users.filter((u) => u.access === role.toLowerCase()).length;
  };

  const handleRoleChange = (role) => {
    setSharedUserRoleFilterName(role.toLowerCase());
  };

  return (
    <>
      <div className={cssClass.accessUserCountCt}>
        <div className={cssClass.accessUserCountInner}>
          <img src={userIcon} alt="" className={cssClass.accessUserUserIcon} />
          <div className={cssClass.accessUserCount}>{users.length}</div>
        </div>
        <div className={cssClass.userSearchCt}>
          <img src={searchIcon} alt="" />
          <input
            type="text"
            className={cssClass.userSearchInput}
            placeholder="Search here...."
            value={searchValue}
            onChange={onSearchChange}
          />

          {resultCount !== null && <p>{resultCount} result</p>}
        </div>
        {hasFilter && (
          <button
            className={`${cssClass.roleFilterButtonCt} ${filterMenuOpen ? cssClass.open : ''}`}
            onClick={() => setFilterMenuOpen(!filterMenuOpen)}
          >
            <img src={filterIcon} alt="" />
          </button>
        )}
      </div>
      <AnimatePresence>
        {hasFilter && filterMenuOpen && (
          <motion.div
            className={cssClass.roleFilterCt}
            variants={filterMenuAnimationVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            {ROLES.map((role) => (
              <div
                key={role}
                onClick={() => handleRoleChange(role)}
                className={`${cssClass.roleCt} ${sharedUserRoleFilterName === role.toLowerCase() ? cssClass.selected : ''}`}
              >
                <p>{role}</p>
                <p>{getUserCountByLevel(role)}</p>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default RoadmapAccessSharing;
